import { isRestOfTheWorld, withoutRestOfTheWorld } from "@signalist/shared-types";

import { ScoreCriteriaType } from "@/pages/Private/pages/Score/schema/score.schema";

/**
 * Reading and writing the rubric's four graded buckets as tier → values, the
 * shape the grading screen edits in. `0` is the disqualify bucket (the scoring
 * engine treats a matched 0 as disqualified); a value listed in no tier at all
 * simply scores nothing.
 */

export const TIERS = [3, 2, 1, 0] as const;

export type Tier = (typeof TIERS)[number];

interface CriterionItem {
	points: number;
	description?: string;
	countries?: string[];
	industries?: string[];
}

/** Which group of the criteria JSON a tier editor reads, and under which field. */
export interface ListCriterionRef {
	section: "companyScoring" | "personaScoring";
	key: "HQ_COUNTRY" | "SUBSIDIARY_COUNTRY" | "INDUSTRY" | "PERSON_LOCATION";
	field: "countries" | "industries";
}

interface CriterionGroup {
	active?: boolean;
	items?: CriterionItem[];
}

const groupOf = (criteria: ScoreCriteriaType, ref: ListCriterionRef): CriterionGroup =>
	(criteria.criteria[ref.section] as unknown as Record<string, CriterionGroup>)[ref.key] ?? {};

/** True when the criterion counts towards the score at all. */
export const isCriterionActive = (criteria: ScoreCriteriaType, ref: ListCriterionRef): boolean =>
	groupOf(criteria, ref).active === true;

/** The values configured for one tier, in the order they were added. */
export const readTierValues = (
	criteria: ScoreCriteriaType,
	ref: ListCriterionRef,
	tier: Tier
): string[] => {
	const items = groupOf(criteria, ref).items ?? [];

	return items
		.filter((item) => item.points === tier)
		.flatMap((item) => item[ref.field] ?? [])
		.filter((value): value is string => typeof value === "string");
};

const withGroup = (
	criteria: ScoreCriteriaType,
	ref: ListCriterionRef,
	patch: Partial<CriterionGroup>
): ScoreCriteriaType => {
	const section = criteria.criteria[ref.section] as unknown as Record<string, CriterionGroup>;

	return {
		...criteria,
		criteria: {
			...criteria.criteria,
			[ref.section]: {
				...section,
				[ref.key]: { ...(section[ref.key] ?? {}), ...patch },
			},
		},
	} as ScoreCriteriaType;
};

/** The other buckets with the catch-all taken out, dropping any left empty. */
const withoutCatchAll = (items: CriterionItem[], ref: ListCriterionRef): CriterionItem[] =>
	items
		.map((item) => ({ ...item, [ref.field]: withoutRestOfTheWorld(item[ref.field] ?? []) }))
		.filter((item) => (item[ref.field] ?? []).length > 0);

/**
 * Replaces one tier's values, collapsing the tier to a single item. The rubric
 * allows several items per bucket, but the tier editor shows one chip row per
 * bucket — so writing it back as one item keeps what the user sees and what is
 * stored identical.
 *
 * The "Rest of the world" catch-all answers "everything you did not list", so
 * two buckets claiming it contradict each other and the scoring engine resolves
 * it arbitrarily (highest wins). Taking it into this tier therefore takes it out
 * of every other one — which is also what makes dragging it between buckets a
 * move rather than a copy.
 */
export const writeTierValues = (
	criteria: ScoreCriteriaType,
	ref: ListCriterionRef,
	tier: Tier,
	values: string[]
): ScoreCriteriaType => {
	const items = groupOf(criteria, ref).items ?? [];
	const existing = items.find((item) => item.points === tier);
	const rest = items.filter((item) => item.points !== tier);
	const others = values.some(isRestOfTheWorld) ? withoutCatchAll(rest, ref) : rest;
	const next =
		values.length > 0
			? [...others, { ...(existing ?? { description: "" }), points: tier, [ref.field]: values }]
			: others;

	// Keep the buckets in display order so a later read renders them the same way.
	next.sort((a, b) => b.points - a.points);

	return withGroup(criteria, ref, { items: next as CriterionItem[] });
};

/** Whether the criterion has any value in any bucket. */
export const hasTierValues = (criteria: ScoreCriteriaType, ref: ListCriterionRef): boolean =>
	(groupOf(criteria, ref).items ?? []).some((item) => (item[ref.field] ?? []).length > 0);

/**
 * Replaces one criterion's buckets with another's, bucket for bucket.
 *
 * The person location is "often, but not always, the company's country", and
 * re-typing twenty-odd countries to say so was the longest piece of manual work
 * on the whole screen. Copying replaces rather than merges: the point is "the
 * same as over there", and a merge would leave a half-and-half rubric nobody
 * asked for.
 */
export const copyTierValues = (
	criteria: ScoreCriteriaType,
	from: ListCriterionRef,
	to: ListCriterionRef
): ScoreCriteriaType =>
	TIERS.reduce(
		(next, tier) => writeTierValues(next, to, tier, readTierValues(criteria, from, tier)),
		criteria
	);

export const setCriterionActive = (
	criteria: ScoreCriteriaType,
	ref: ListCriterionRef,
	active: boolean
): ScoreCriteriaType => withGroup(criteria, ref, { active });
